import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Mail, MessageCircle, TrendingUp, Lightbulb, Briefcase } from 'lucide-react';
import content from '../data/content.json';
import { calculateAuditResults, generatePDFData } from '../utils/auditCalculations';
import { generateAuditPDF, generateConsultationEmail } from '../utils/pdfGenerator';
import AuditReportPrint from '../components/AuditReportPrint';

// WhatsApp number - update this
const WHATSAPP_NUMBER = "919324280636";

// Map vertical URL params to Q1 industry options
const VERTICAL_TO_INDUSTRY = {
  'ca-firms': 'CA / Accounting Firms',
  'clinics': 'Healthcare / Clinic',
  'real-estate': 'Real Estate',
  'agencies': 'Agency / Consulting',
  'legal': 'Legal / Law Firms'
};

const FreeAudit = () => {
  const { hero, results: resultsContent, questions: defaultQuestions, userInfoStep, verticalQuestions } = content.freeAudit;
  const [searchParams] = useSearchParams();
  const vertical = searchParams.get('vertical');
  const preSelectedIndustry = VERTICAL_TO_INDUSTRY[vertical] || null;

  // Use vertical-specific questions if available, otherwise use default questions
  const hasVerticalQuestions = vertical && verticalQuestions && verticalQuestions[vertical];
  const questions = hasVerticalQuestions ? verticalQuestions[vertical] : defaultQuestions;

  // For vertical audits: start at step 0 (first vertical question), industry is pre-set
  // For main site: start at step 0 (industry selection)
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState(preSelectedIndustry ? { industry: preSelectedIndustry } : {});
  const [otherText, setOtherText] = useState('');
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    company: '',
    phone: ''
  });
  const [auditResults, setAuditResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const totalSteps = questions.length + 1;

  const handleMultipleChoice = (questionId, option) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: option
    }));
    // Reset other text when a non-Other option is selected
    if (option !== 'Other') {
      setOtherText('');
    }
  };

  const handleCheckboxChange = (questionId, option) => {
    setAnswers(prev => {
      const currentAnswers = prev[questionId] || [];
      const updatedAnswers = currentAnswers.includes(option)
        ? currentAnswers.filter(item => item !== option)
        : [...currentAnswers, option];

      return {
        ...prev,
        [questionId]: updatedAnswers
      };
    });
  };

  const handleTextInput = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    // Build normalized answers for calculation
    let finalAnswers;

    if (hasVerticalQuestions) {
      if (vertical === 'ca-firms') {
        // CA has 5 questions: team(1), pains(2), practice area(3), software(4), urgency(5)
        finalAnswers = {
          1: answers.industry,
          2: answers[1], // Team size from Q1
          3: answers[2], // Pain points from Q2
          4: answers[3], // Practice area from Q3 (used as department)
          5: answers[5], // Urgency from Q5
          software: answers[4], // Software from Q4
          vertical: vertical
        };
      } else {
        // Other verticals: team(1), pains(2), workstream(3), urgency(4)
        finalAnswers = {
          1: answers.industry,
          2: answers[1],
          3: answers[2],
          4: answers[3],
          5: answers[4],
          vertical: vertical
        };
      }
    } else {
      // Default audit flow
      finalAnswers = { ...answers };
      if (answers[1] === 'Other' && otherText.trim()) {
        finalAnswers[1] = otherText.trim();
        finalAnswers.isCustomIndustry = true;
      }
    }

    const calculatedResults = calculateAuditResults(finalAnswers, userInfo, vertical);

    // Attach software info for CA vertical (used in WhatsApp message and results)
    if (finalAnswers.software) {
      calculatedResults.software = finalAnswers.software;
    }

    // Save audit submission to backend
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    if (apiUrl) {
      try {
        await fetch(`${apiUrl}/api/audit-submissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_info: userInfo,
            answers: finalAnswers,
            results: {
              timeSavingsPotential: calculatedResults.timeSavingsPotential,
              costReductionPotential: calculatedResults.costReductionPotential,
              automationScore: calculatedResults.automationScore,
              priorityAreas: calculatedResults.prioritySolutions || [],
              recommendedNextSteps: calculatedResults.recommendations || [],
              userInfo: userInfo,
            },
          }),
        });
      } catch (err) {
        console.error('Failed to save audit submission:', err);
      }
    }

    setAuditResults(calculatedResults);
    // Generate PDF data for the print component
    const generatedPdfData = generatePDFData(calculatedResults);
    setPdfData(generatedPdfData);
    setIsSubmitting(false);
    setShowResults(true);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDownloadPDF = async () => {
    if (auditResults && pdfData) {
      try {
        setIsGeneratingPDF(true);
        // Small delay to ensure the print component is rendered
        await new Promise(resolve => setTimeout(resolve, 100));
        await generateAuditPDF(pdfData);
        setIsGeneratingPDF(false);
      } catch (error) {
        console.error('Error generating PDF:', error);
        setIsGeneratingPDF(false);
        alert('There was an issue generating your PDF. Please try again.');
      }
    }
  };

  const handleScheduleConsultation = () => {
    if (auditResults) {
      const pdfData = generatePDFData(auditResults);
      generateConsultationEmail(pdfData);

      // Save contact submission to backend
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      if (apiUrl) {
        fetch(`${apiUrl}/api/contact-submissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: userInfo.name,
            email: userInfo.email,
            company: userInfo.company,
            phone: userInfo.phone,
            subject: 'Consultation Request - Automation Audit Follow-up',
            message: `I completed the automation audit and received a score of ${auditResults.automationScore}. I would like to schedule a consultation to discuss the automation opportunities for ${userInfo.company}.`,
          }),
        }).catch(err => console.error('Failed to save contact submission:', err));
      }
    }
  };

  const handleWhatsAppChat = () => {
    if (auditResults) {
      const softwareLine = auditResults.software ? `- Software: ${auditResults.software}\n` : '';
      const message = encodeURIComponent(
        `Hi! I just completed the automation audit for ${auditResults.userInfo.company}.\n\n` +
        `My Results:\n` +
        `- Automation Score: ${auditResults.automationScore}/100\n` +
        `- Time Savings: ${auditResults.timeSavingsPotential}\n` +
        `- Cost Savings: ${auditResults.costReductionPotential}\n` +
        `- Focus Area: ${auditResults.department}\n` +
        softwareLine + `\n` +
        `I'd like to discuss how to implement automation for my business.`
      );
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
    }
  };

  // Calculate remaining questions from current position
  const questionsRemaining = totalSteps - currentStep - 1;

  const getProgress = () => {
    return ((currentStep + 1) / totalSteps) * 100;
  };

  const isCurrentStepComplete = () => {
    if (currentStep === totalSteps - 1) {
      return userInfo.name && userInfo.email && userInfo.company;
    } else {
      const question = questions[currentStep];
      const questionId = question.id;

      // Text inputs are optional
      if (question.type === 'text') {
        return true;
      }

      return answers[questionId] &&
             (Array.isArray(answers[questionId]) ? answers[questionId].length > 0 : true);
    }
  };

  if (showResults) {
    return (
      <div className="min-h-screen pt-20">
        <section className="pad-2xl">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              {/* Success Header */}
              <div className="text-center mb-12">
                <div className="flex justify-center mb-4">
                  <CheckCircle className="h-16 w-16" style={{ color: 'var(--accent-text)' }} />
                </div>
                <h1 className="heading-1 mb-4">{resultsContent.title}</h1>
                <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                  {auditResults.goalMessage || resultsContent.subtitle}
                </p>
              </div>

              {/* Industry Benchmark Banner */}
              {auditResults.industryBenchmark && (
                <div
                  className="p-6 rounded-xl mb-8"
                  style={{ background: 'linear-gradient(135deg, #1E40AF 0%, #EA6D2B 100%)' }}
                >
                  <div className="flex items-start gap-4">
                    <TrendingUp className="h-8 w-8 text-white flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="heading-3 mb-2" style={{ color: 'white' }}>
                        {auditResults.isCustomIndustry
                          ? 'How businesses like yours are winning with automation'
                          : `How ${auditResults.industry} businesses are winning with automation`}
                      </h3>
                      <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {auditResults.industryBenchmark.caseStudy}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Department Focus - Quick Win */}
              {auditResults.departmentQuickWin && (
                <div
                  className="p-6 rounded-xl mb-8 border-l-4"
                  style={{ background: '#ECFDF5', borderColor: '#10B981' }}
                >
                  <div className="flex items-start gap-4">
                    <Briefcase className="h-6 w-6 flex-shrink-0 mt-1" style={{ color: '#10B981' }} />
                    <div>
                      <h3 className="heading-4 mb-2" style={{ color: '#065F46' }}>
                        Quick Win for {auditResults.department}
                      </h3>
                      <p className="body-medium" style={{ color: '#047857' }}>
                        {auditResults.departmentQuickWin} - {auditResults.departmentImpact}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Results Grid */}
              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="product-card">
                  <h3 className="heading-3 mb-4" style={{ color: 'var(--accent-text)' }}>
                    {resultsContent.timeSavingsLabel}
                  </h3>
                  <div className="text-center">
                    <div className="heading-1 mb-2" style={{ color: 'var(--accent-text)' }}>
                      {auditResults.timeSavingsPotential}
                    </div>
                    <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                      {resultsContent.timeSavingsNote}
                    </p>
                  </div>
                </div>

                <div className="product-card">
                  <h3 className="heading-3 mb-4" style={{ color: 'var(--accent-text)' }}>
                    {resultsContent.costReductionLabel}
                  </h3>
                  <div className="text-center">
                    <div className="heading-1 mb-2" style={{ color: 'var(--accent-text)' }}>
                      {auditResults.costReductionPotential}
                    </div>
                    <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                      {resultsContent.costReductionNote}
                    </p>
                  </div>
                </div>

                <div className="product-card md:col-span-2">
                  <h3 className="heading-3 mb-4" style={{ color: 'var(--accent-text)' }}>
                    {resultsContent.scoreLabel}
                  </h3>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div
                        className="h-4 rounded-full"
                        style={{ background: 'var(--accent-wash)' }}
                      >
                        <div
                          className="h-4 rounded-full transition-all duration-1000"
                          style={{
                            background: 'var(--gradient-button)',
                            width: `${auditResults.automationScore}%`
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="heading-2" style={{ color: 'var(--accent-text)' }}>
                      {auditResults.automationScore}/100
                    </div>
                  </div>
                  <p className="body-medium mt-2" style={{ color: 'var(--text-secondary)' }}>
                    {auditResults.automationScore >= 80
                      ? "Your business has excellent automation potential - let's unlock it!"
                      : auditResults.automationScore >= 70
                      ? "Strong potential for automation - a few key changes could transform your operations."
                      : "Good automation opportunities identified - let's put them into action."}
                  </p>
                </div>
              </div>

              {/* Key Insights */}
              {auditResults.keyInsights && auditResults.keyInsights.length > 0 && (
                <div className="product-card mb-12">
                  <div className="flex items-center gap-3 mb-6">
                    <Lightbulb className="h-6 w-6" style={{ color: 'var(--accent-text)' }} />
                    <h3 className="heading-3">What We Noticed</h3>
                  </div>
                  <div className="space-y-4">
                    {auditResults.keyInsights.map((insight, index) => (
                      <div
                        key={index}
                        className="p-4 rounded-lg"
                        style={{ background: 'var(--accent-wash)' }}
                      >
                        <p className="body-medium" style={{ color: 'var(--text-body)' }}>
                          {insight}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Priority Solutions */}
              {auditResults.prioritySolutions && auditResults.prioritySolutions.length > 0 && (
                <div className="product-card mb-12">
                  <h3 className="heading-3 mb-6">{resultsContent.priorityAreasTitle}</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {auditResults.prioritySolutions.map((solution, index) => (
                      <div
                        key={index}
                        className="p-4 rounded-lg text-center"
                        style={{ background: 'var(--accent-wash)' }}
                      >
                        <div className="heading-4" style={{ color: 'var(--accent-text)' }}>
                          {solution}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Industry-Specific Solutions */}
              {auditResults.industrySpecificSolutions && auditResults.industrySpecificSolutions.length > 0 && (
                <div className="product-card mb-12">
                  <h3 className="heading-3 mb-6">
                    Tailored for {auditResults.isCustomIndustry ? 'Your Business' : auditResults.industry}
                  </h3>
                  <ul className="space-y-4">
                    {auditResults.industrySpecificSolutions.map((solution, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0 mt-2"
                          style={{ background: 'var(--gradient-button)' }}
                        ></div>
                        <p className="body-medium" style={{ color: 'var(--text-body)' }}>
                          {solution}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              <div className="product-card mb-12">
                <h3 className="heading-3 mb-6">{resultsContent.nextStepsTitle}</h3>
                <ul className="space-y-4">
                  {auditResults.recommendations.map((step, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-0.5"
                        style={{ background: 'var(--gradient-button)' }}
                      >
                        {index + 1}
                      </div>
                      <p className="body-medium" style={{ color: 'var(--text-body)' }}>
                        {step}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Section */}
              <div className="text-center">
                <h2 className="heading-2 mb-4">{resultsContent.ctaTitle}</h2>
                <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
                  {resultsContent.ctaSubtitle}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                  <button
                    onClick={handleWhatsAppChat}
                    className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold transition-all hover:opacity-90"
                    style={{ background: '#25D366', color: 'white' }}
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    {resultsContent.whatsappButton || "Chat on WhatsApp"}
                  </button>
                  <button onClick={handleScheduleConsultation} className="btn-orange">
                    <Mail className="mr-2 h-5 w-5" />
                    {resultsContent.consultationButton}
                  </button>
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={handleDownloadPDF}
                    disabled={isGeneratingPDF}
                    className={`btn-secondary ${isGeneratingPDF ? 'opacity-50 cursor-wait' : ''}`}
                  >
                    <Download className="mr-2 h-5 w-5" />
                    {isGeneratingPDF ? 'Generating PDF...' : resultsContent.downloadButton}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Hidden print component for PDF generation */}
        {pdfData && (
          <div
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '210mm',
              background: 'white'
            }}
          >
            <AuditReportPrint data={pdfData} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      <section className="pad-2xl">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="heading-1 mb-4">{hero.title}</h1>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                {hero.subtitle}
              </p>

              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex justify-between text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
                  <span>{questionsRemaining === 0 ? 'Last step!' : `${questionsRemaining} question${questionsRemaining === 1 ? '' : 's'} to go`}</span>
                  <span>{Math.round(getProgress())}%</span>
                </div>
                <div
                  className="h-2 rounded-full"
                  style={{ background: 'var(--accent-wash)' }}
                >
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      background: 'var(--gradient-button)',
                      width: `${getProgress()}%`
                    }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Question Content */}
            <div className="product-card">
              {currentStep < questions.length ? (
                <div>
                  <h2 className="heading-2 mb-2">
                    {questions[currentStep].question}
                  </h2>

                  {/* Subtext if available */}
                  {questions[currentStep].subtext && (
                    <p className="body-medium mb-6" style={{ color: 'var(--text-muted)' }}>
                      {questions[currentStep].subtext}
                    </p>
                  )}

                  {questions[currentStep].type === 'multiple-choice' && (
                    <div className="space-y-3">
                      {questions[currentStep].options.map((option, index) => (
                        <div key={index}>
                          <button
                            onClick={() => handleMultipleChoice(questions[currentStep].id, option)}
                            className={`w-full p-4 text-left border rounded-lg transition-all ${
                              answers[questions[currentStep].id] === option
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-5 h-5 rounded-full border-2 transition-all flex items-center justify-center ${
                                  answers[questions[currentStep].id] === option
                                    ? 'border-green-500 bg-green-500'
                                    : 'border-gray-300'
                                }`}
                              >
                                {answers[questions[currentStep].id] === option && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                              <span className="body-medium">{option}</span>
                            </div>
                          </button>
                          {/* Show text input when "Other" is selected */}
                          {option === 'Other' && answers[questions[currentStep].id] === 'Other' && questions[currentStep].allowOtherText && (
                            <div className="mt-3 ml-8">
                              <input
                                type="text"
                                value={otherText}
                                onChange={(e) => setOtherText(e.target.value)}
                                className="w-full px-4 py-3 border rounded-lg"
                                style={{
                                  borderColor: 'var(--border-light)',
                                  background: 'var(--bg-card)'
                                }}
                                placeholder="Please specify your industry..."
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {questions[currentStep].type === 'checkbox' && (
                    <div className="space-y-3">
                      {questions[currentStep].options.map((option, index) => (
                        <button
                          key={index}
                          onClick={() => handleCheckboxChange(questions[currentStep].id, option)}
                          className={`w-full p-4 text-left border rounded-lg transition-all ${
                            answers[questions[currentStep].id]?.includes(option)
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center ${
                                answers[questions[currentStep].id]?.includes(option)
                                  ? 'border-green-500 bg-green-500'
                                  : 'border-gray-300'
                              }`}
                            >
                              {answers[questions[currentStep].id]?.includes(option) && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <span className="body-medium">{option}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {questions[currentStep].type === 'text' && (
                    <div className="space-y-4">
                      <textarea
                        value={answers[questions[currentStep].id] || ''}
                        onChange={(e) => handleTextInput(questions[currentStep].id, e.target.value)}
                        className="w-full px-4 py-3 border rounded-lg resize-none"
                        style={{
                          borderColor: 'var(--border-light)',
                          background: 'var(--bg-card)',
                          minHeight: '120px'
                        }}
                        placeholder={questions[currentStep].placeholder || 'Type your answer here...'}
                      />
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                        This is optional - but the more specific you are, the better recommendations we can give you.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <h2 className="heading-2 mb-2">{userInfoStep.title}</h2>
                  <p className="body-medium mb-6" style={{ color: 'var(--text-muted)' }}>
                    We'll send your personalised report to this email
                  </p>
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="body-medium font-medium mb-2 block">
                          {userInfoStep.fields.name} *
                        </label>
                        <input
                          type="text"
                          value={userInfo.name}
                          onChange={(e) => setUserInfo(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-3 border rounded-lg"
                          style={{
                            borderColor: 'var(--border-light)',
                            background: 'var(--bg-card)'
                          }}
                          placeholder="Enter your full name"
                        />
                      </div>
                      <div>
                        <label className="body-medium font-medium mb-2 block">
                          {userInfoStep.fields.email} *
                        </label>
                        <input
                          type="email"
                          value={userInfo.email}
                          onChange={(e) => setUserInfo(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-4 py-3 border rounded-lg"
                          style={{
                            borderColor: 'var(--border-light)',
                            background: 'var(--bg-card)'
                          }}
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="body-medium font-medium mb-2 block">
                          {userInfoStep.fields.company} *
                        </label>
                        <input
                          type="text"
                          value={userInfo.company}
                          onChange={(e) => setUserInfo(prev => ({ ...prev, company: e.target.value }))}
                          className="w-full px-4 py-3 border rounded-lg"
                          style={{
                            borderColor: 'var(--border-light)',
                            background: 'var(--bg-card)'
                          }}
                          placeholder="Enter your company name"
                        />
                      </div>
                      <div>
                        <label className="body-medium font-medium mb-2 block">
                          {userInfoStep.fields.phone}
                        </label>
                        <input
                          type="tel"
                          value={userInfo.phone}
                          onChange={(e) => setUserInfo(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-4 py-3 border rounded-lg"
                          style={{
                            borderColor: 'var(--border-light)',
                            background: 'var(--bg-card)'
                          }}
                          placeholder="Enter your phone number"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-8">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className={`btn-secondary ${currentStep === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Previous
              </button>

              {currentStep < totalSteps - 1 ? (
                <button
                  onClick={handleNext}
                  disabled={!isCurrentStepComplete()}
                  className={`btn-orange ${!isCurrentStepComplete() ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {questions[currentStep]?.type === 'text' && !answers[questions[currentStep]?.id]
                    ? 'Skip'
                    : 'Next'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!isCurrentStepComplete() || isSubmitting}
                  className={`btn-orange ${(!isCurrentStepComplete() || isSubmitting) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Generating Report...' : 'Get My Report'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreeAudit;
